import { useState, useEffect } from "react";
import { marked } from "marked";
import { invoke } from "../lib/bridge";
import { helpPages, type HelpPage } from "../lib/helpPages";
import aboutMd from "../help/about.md?raw";
import userMd from "../help/user.md?raw";
import privacyMd from "../help/privacy.md?raw";
import tosMd from "../help/tos.md?raw";
import securityMd from "../help/security.md?raw";

const pageContent: Record<HelpPage, string> = {
  about: aboutMd,
  user: userMd,
  security: securityMd,
  privacy: privacyMd,
  tos: tosMd,
};

// Help pages contain static, bundled markdown authored by the dev team, not user input.
function renderHelpMarkdown(md: string): string {
  return marked.parse(md) as string;
}

interface VersionInfo {
  version: string;
  build: string | null;
  upToDate: boolean | null;
}

export function HelpView({ onBack }: { onBack: () => void }) {
  const [activePage, setActivePage] = useState<HelpPage | null>(null);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    invoke<VersionInfo>("app_version").then(setVersionInfo).catch(() => {});
  }, []);

  if (activePage) {
    const content = pageContent[activePage];
    return (
      <div className="container">
        <button className="btn-back" onClick={() => setActivePage(null)}>&#8249; Help</button>
        <div
          className="note-markdown"
          style={{ paddingBottom: "24px", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.44em" }}
          dangerouslySetInnerHTML={{ __html: renderHelpMarkdown(content) }}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: "16px" }}>
      <div className="header-row">
        <h1 className="header-title">Help</h1>
        <button onClick={onBack} className="btn-close">&times;</button>
      </div>

      <ul className="help-toc">
        {helpPages.map((item) => (
          <li key={item.page} className="help-toc-item" onClick={() => setActivePage(item.page)}>
            <div className="help-toc-text">
              <span className="help-toc-title">{item.title}</span>
              <span className="help-toc-subtitle">{item.subtitle}</span>
            </div>
            <span className="member-chevron">&rsaquo;</span>
          </li>
        ))}
      </ul>

      {versionInfo && (
        <div
          style={{
            marginTop: "32px",
            textAlign: "center",
            fontSize: "12px",
            lineHeight: "1.6",
            opacity: 0.55,
          }}
        >
          <div>
            Nuthatch Family v{versionInfo.version}
            {versionInfo.build ? ` · build ${versionInfo.build}` : ""}
          </div>
          {versionInfo.upToDate === true && <div>&#10003; Up to date</div>}
          {versionInfo.upToDate === false && (
            <div>Update available: fully close and reopen the app to update</div>
          )}
        </div>
      )}
    </div>
  );
}
